import { useSelector } from 'react-redux';
import TitleHeader from '../../components/MasterComponent/TitleHeader/TitleHeader';
import { SelectedAccount } from '../../components/SelectAccount/SelectAccount';
import { CLOUD_PROVIDER_SERVICES } from '../../utils/constants';
import { useFetchData } from '../../hooks/useFetchData';
import { config } from './config';
import { exportToFile } from '../../utils/formateXLSX';
import { accountState, RootState } from '../../store';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './cost-breakup.css';
import Header from '../../components/CostBreakup/Header';
import { MultilevelHeaderTable } from '../../components/CostBreakup/MultilevelHeaderTable';
import { ALL_VALUE } from '../Invoice/config';
import { SECURITY_TABS, SecurityTabs, SECURITY_REPORT_MAP } from './costBreakupTabs';
import { COST_BREAKUP_FIELD_LABELS, SECURITY_SUMMABLE_COLUMNS_BY_TAB } from './costBreakupTableColumns';
import CustomSnackBar from '../../components/MasterComponent/CustomSnackbar/CustomSnackBar';

const SecurityCostBreakup: React.FC<any> = () => {
  const selectedAccountInfo = useSelector(accountState);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [securityCostData, setSecurityCostData] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = React.useState<any>(
    selectedAccountInfo.selectedAccountId
  );
  const [selectedTab, setSelectedTab] = useState<SecurityTabs>(SECURITY_TABS[0]);
  const currentTabRef = useRef<SecurityTabs>(selectedTab);
  const selectedMonth = useSelector((state: RootState) => state.month.selectedMonth);
  const [selectedMonthName, selectedYear] = selectedMonth.split(' ');
  const monthNumber = new Date(`${selectedMonthName} 1, ${selectedYear}`).getMonth() + 1;
  const getCacheKey = (tab: SecurityTabs, accountId: string, month: string) =>
  `${tab}_${accountId}_${month}`;
  const cacheRef = useRef<Map<string, any>>(new Map());
  const formattedMonth = monthNumber.toString().padStart(2, '0');
  const [sortColumn, setSortColumn] = useState<string>('');
  const reqBodyRef = useRef({
    month: formattedMonth,
    service: SECURITY_REPORT_MAP[selectedTab],
    reportType: selectedTab,
    year: selectedYear,
    ...(selectedAccountId !== ALL_VALUE
      ? { accountId: selectedAccountId }
      : {}),
  });

  const { fetch, data, loader: isSubmitting } = useFetchData({
    fetchData: () => config.getSecurityReport(reqBodyRef.current),

    fetchOnMount: false,
  });

  const onAccountChange = (data: any) => {
    if (data.cloudType === CLOUD_PROVIDER_SERVICES.AWS)
      setSelectedAccountId(data.accountId);
    // else if (data.cloudType === CLOUD_PROVIDER_SERVICES.AZURE) {
    //   if (data.subscriptionName) {
    //     setSelectedSubscriptionId(data.subscriptionId);
    //   } else if (data.accountId) {
    //     setSelectedAccountId(data.accountId);
    //     setSelectedSubscriptionId(ALL_VALUE);
    //   }
    // }
  };

  // Handle sorting
  const handleSort = (column: string) => {
    const newDirection =
      sortColumn === column && sortDirection === 'desc' ? 'asc' : 'desc';

    setSortColumn(column);
    setSortDirection(newDirection);
  };

  const handleTabClick = (newTab: SecurityTabs) => {
    setSelectedTab(newTab);
  };

  const downloadCsvHandler = () => {
    fetchSecurityCostReport();
  };

  const {
    fetch: fetchSecurityCostReport,
    data: fetchCostReportDownloadStatus,
    loader: isDownloadLoading,
    error: fetchCostReportDownloadError,
  } = useFetchData({
    fetchData: () => config.downloadSecurityReport(reqBodyRef.current),
    fetchOnMount: false,
  });
  
  const sortedServiceEntries = useMemo(() => {
    const serviceEntries = Object.entries(securityCostData ?? {}).filter(
      ([key]) => key !== 'Total Cost'
    ) as [string, Record<string, any>][];
    
    if (!sortColumn) return serviceEntries;

    return [...serviceEntries].sort((a, b) => {
      let valueA = a[1][sortColumn];
      let valueB = b[1][sortColumn];

      // Null / undefined fallback
      if (valueA === undefined || valueA === null) valueA = '';
      if (valueB === undefined || valueB === null) valueB = '';

      // If both are numbers
      if (!isNaN(Number(valueA)) && !isNaN(Number(valueB))) {
        return sortDirection === 'asc'
          ? Number(valueA) - Number(valueB)
          : Number(valueB) - Number(valueA);
      }

      // Otherwise compare as strings
      const strA = String(valueA).toLowerCase();
      const strB = String(valueB).toLowerCase();
      return sortDirection === 'asc'
        ? strA.localeCompare(strB)
        : strB.localeCompare(strA);
    });
  }, [securityCostData, sortColumn, sortDirection]);

  const columns = useMemo(() => {
    if (!securityCostData || securityCostData.length === 0) return [];

    // Collect all unique keys across all entries
    const allKeysSet = new Set<string>();
    for (const item of securityCostData) {
      Object.keys(item).forEach((key) => allKeysSet.add(key));
    }

    return Array.from(allKeysSet).map((key) => ({
      key,
      label: COST_BREAKUP_FIELD_LABELS.find((f) => f.key === key)?.label ?? key,
    }));
  }, [securityCostData]);

  const summableColumns = useMemo(() => {
    return SECURITY_SUMMABLE_COLUMNS_BY_TAB[selectedTab] ?? [];
  }, [selectedTab]);

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'info' | 'success' | 'error' | 'warning'>('info');

  const handleSnackBarClose = () => {
    setSnackBarOpen(false);
  };

  useEffect(() => {
    const cacheKey = getCacheKey(selectedTab, selectedAccountId, selectedMonth);
    reqBodyRef.current = {
      month: formattedMonth,
      service: SECURITY_REPORT_MAP[selectedTab],
      reportType: selectedTab,
      year: selectedYear,
      ...(selectedAccountId !== ALL_VALUE
        ? { accountId: selectedAccountId }
        : {}),
    };
    if (cacheRef.current.has(cacheKey)) {
      setSecurityCostData(cacheRef.current.get(cacheKey));
    } else {
      setSecurityCostData([]);
      fetch();
    }
  }, [selectedTab, selectedAccountId, selectedMonth]);


  let isCanceled = false;

  useEffect(() => {
    if (!isSubmitting && !isCanceled) {
      const cacheKey = getCacheKey(selectedTab, selectedAccountId, selectedMonth);
      if (reqBodyRef.current.service === SECURITY_REPORT_MAP[currentTabRef.current]) {
        cacheRef.current.set(cacheKey, data ?? []);
        setSecurityCostData(data ?? []);
      }
    }

    return () => {
      isCanceled = true;
    };
  }, [data, isSubmitting, selectedTab, selectedAccountId, selectedMonth]);

  useEffect(() => {
    cacheRef.current.clear();
  }, [selectedAccountId, selectedMonth]);


  useEffect(() => {
    currentTabRef.current = selectedTab;
  }, [selectedTab]);

  useEffect(() => {
    if (!isDownloadLoading) {
      if (fetchCostReportDownloadError) {
        const errorMessage =
          fetchCostReportDownloadError?.error?.message || 'An error occurred while exporting.';
        setSnackBarMessage(errorMessage);
        setSnackBarOpen(true);
        setSnackbarSeverity('error');
      } else if (fetchCostReportDownloadStatus) {
        const { fileBuffer, fileName, message } = fetchCostReportDownloadStatus;

        if (fileBuffer && fileName) {
          exportToFile(fileBuffer, fileName);
        } else if (message === 'No data available to export') {
          setSnackBarMessage('No data available to export.');
          setSnackBarOpen(true);
          setSnackbarSeverity('info');
        }
      }
    }
  }, [isDownloadLoading, fetchCostReportDownloadStatus, fetchCostReportDownloadError]);
    
  return (
    <div className='app-container'>
      <CustomSnackBar
        message={snackBarMessage}
        onClose={handleSnackBarClose}
        open={snackBarOpen}
        severity={snackbarSeverity}
      />
      <TitleHeader
        extraContent={<SelectedAccount onAccountChange={onAccountChange} />}
      >
        Security
      </TitleHeader>
      <Header
        tabs={[...SECURITY_TABS]}
        selectedTab={selectedTab}
        onTabChange={handleTabClick}
        showMonthFilter
        showExcelDownload
        onDownload={downloadCsvHandler}
        isDownloading={isDownloadLoading}
      />
      <MultilevelHeaderTable
        loading={isSubmitting}
        sortedEntries={sortedServiceEntries}
        onSort={handleSort}
        columns={columns}
        summableColumns={summableColumns}
        sortColumn={sortColumn}            
        sortDirection={sortDirection}       
      />
            
    </div>
  );
};

export default SecurityCostBreakup;
